/**
 * Alert.web.js — web/Electron polyfill for React Native's `Alert.alert`.
 *
 * react-native-web ships an Alert that is literally `static alert() {}` — a
 * silent no-op. The product calls Alert.alert in ~150 places (confirmations,
 * error messages, destructive actions, billing-limit prompts, etc.), so on
 * desktop the user taps a button and "nothing happens".
 *
 * This polyfill renders a lightweight DOM-only modal that matches React
 * Native's Alert API closely enough that no call site needs to change:
 *
 *   Alert.alert(title, message?, buttons?, options?)
 *
 * Behaviour:
 *   - 0 buttons         → a single "OK" button is shown.
 *   - 1+ buttons        → rendered in a row with iOS-style separation.
 *   - { style: 'cancel'} → grey, returned on Escape / backdrop click /
 *                          when `options.cancelable !== false`.
 *   - { style: 'destructive' } → red, on the right.
 *   - Backdrop click and Escape both dismiss as cancel.
 *
 * The modal is styled with the same spacing / radius / weight tokens we
 * use elsewhere on desktop, with a `prefers-color-scheme: dark` branch so
 * it doesn't fight the user's system appearance.
 *
 * Implementation notes:
 *   - Pure DOM, no React. Avoids needing a global provider/portal mount in
 *     App.tsx, and works from any code path including module-init time.
 *   - Stylesheet is injected once per page and reused.
 *   - Concurrent alerts are queued and rendered serially — RN does not
 *     define behaviour here, but stacking would cover one prompt with
 *     another and surprise the user.
 */

const STYLE_TAG_ID = '__eleplan_alert_styles';
const ROOT_ID = '__eleplan_alert_root';

const STYLE_RULES = `
  #${ROOT_ID} {
    position: fixed;
    inset: 0;
    z-index: 2147483600;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(15, 17, 22, 0.45);
    -webkit-backdrop-filter: saturate(140%) blur(2px);
    backdrop-filter: saturate(140%) blur(2px);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #111417;
    animation: __eleplan_alert_fade_in 120ms ease-out;
  }
  #${ROOT_ID} .__eleplan_alert_card {
    min-width: 280px;
    max-width: 420px;
    width: 100%;
    background: #ffffff;
    border-radius: 14px;
    box-shadow:
      0 1px 1px rgba(0,0,0,0.06),
      0 18px 48px rgba(15, 17, 22, 0.32);
    overflow: hidden;
    transform: translateY(0) scale(1);
    animation: __eleplan_alert_pop 140ms cubic-bezier(0.18, 0.95, 0.4, 1.05);
  }
  #${ROOT_ID} .__eleplan_alert_body {
    padding: 20px 22px 18px;
    text-align: center;
  }
  #${ROOT_ID} .__eleplan_alert_title {
    font-size: 17px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.01em;
    margin: 0 0 6px;
  }
  #${ROOT_ID} .__eleplan_alert_message {
    font-size: 14px;
    line-height: 19px;
    color: #4a4f57;
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  #${ROOT_ID} .__eleplan_alert_actions {
    display: flex;
    flex-direction: column;
    border-top: 1px solid rgba(15, 17, 22, 0.08);
  }
  #${ROOT_ID} .__eleplan_alert_actions[data-row="1"] {
    flex-direction: row;
  }
  #${ROOT_ID} button.__eleplan_alert_btn {
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    border: 0;
    padding: 13px 14px;
    font-size: 16px;
    font-weight: 500;
    color: #ed5b3a;
    cursor: pointer;
    flex: 1;
    text-align: center;
    transition: background-color 80ms ease;
    font-family: inherit;
  }
  #${ROOT_ID} .__eleplan_alert_actions[data-row="1"] > button + button {
    border-left: 1px solid rgba(15, 17, 22, 0.08);
  }
  #${ROOT_ID} .__eleplan_alert_actions[data-row="0"] > button + button {
    border-top: 1px solid rgba(15, 17, 22, 0.08);
  }
  #${ROOT_ID} button.__eleplan_alert_btn:hover {
    background: rgba(15, 17, 22, 0.04);
  }
  #${ROOT_ID} button.__eleplan_alert_btn:active {
    background: rgba(15, 17, 22, 0.08);
  }
  #${ROOT_ID} button.__eleplan_alert_btn[data-style="cancel"] {
    color: #4a4f57;
    font-weight: 400;
  }
  #${ROOT_ID} button.__eleplan_alert_btn[data-style="destructive"] {
    color: #d92d20;
    font-weight: 600;
  }
  #${ROOT_ID} button.__eleplan_alert_btn[data-emphasis="primary"] {
    font-weight: 600;
  }
  @keyframes __eleplan_alert_fade_in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes __eleplan_alert_pop {
    from { opacity: 0; transform: translateY(6px) scale(0.96); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @media (prefers-color-scheme: dark) {
    #${ROOT_ID} {
      color: #f3f4f6;
      background: rgba(0, 0, 0, 0.55);
    }
    #${ROOT_ID} .__eleplan_alert_card {
      background: #1d1f23;
      box-shadow:
        0 1px 1px rgba(0,0,0,0.4),
        0 18px 48px rgba(0, 0, 0, 0.6);
    }
    #${ROOT_ID} .__eleplan_alert_message { color: #b8bcc4; }
    #${ROOT_ID} .__eleplan_alert_actions { border-top-color: rgba(255,255,255,0.08); }
    #${ROOT_ID} .__eleplan_alert_actions[data-row="1"] > button + button {
      border-left-color: rgba(255,255,255,0.08);
    }
    #${ROOT_ID} .__eleplan_alert_actions[data-row="0"] > button + button {
      border-top-color: rgba(255,255,255,0.08);
    }
    #${ROOT_ID} button.__eleplan_alert_btn[data-style="cancel"] { color: #b8bcc4; }
    #${ROOT_ID} button.__eleplan_alert_btn:hover { background: rgba(255,255,255,0.06); }
    #${ROOT_ID} button.__eleplan_alert_btn:active { background: rgba(255,255,255,0.10); }
  }
`;

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = STYLE_RULES;
  document.head.appendChild(style);
}

// One-at-a-time queue. RN doesn't define stacking behaviour for
// concurrent Alert.alert calls; serializing matches the iOS feel and
// avoids confusing overlap.
const queue = [];
let isShowing = false;

function processNext() {
  if (isShowing) return;
  const next = queue.shift();
  if (!next) return;
  isShowing = true;
  show(next);
}

function findCancelButton(buttons) {
  return buttons.find((b) => b && b.style === 'cancel') || null;
}

function show({ title, message, buttons, options }) {
  injectStyles();

  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');

  const card = document.createElement('div');
  card.className = '__eleplan_alert_card';

  const body = document.createElement('div');
  body.className = '__eleplan_alert_body';

  if (title) {
    const t = document.createElement('h2');
    t.className = '__eleplan_alert_title';
    t.textContent = title;
    body.appendChild(t);
  }
  if (message) {
    const m = document.createElement('p');
    m.className = '__eleplan_alert_message';
    m.textContent = message;
    body.appendChild(m);
  }
  card.appendChild(body);

  const actions = document.createElement('div');
  actions.className = '__eleplan_alert_actions';
  // Row layout looks right for 1–2 buttons; stack vertically beyond that
  // (iOS does the same — three or more options always stack).
  actions.dataset.row = buttons.length <= 2 ? '1' : '0';

  const dismiss = (chosen) => {
    if (root.parentNode) root.parentNode.removeChild(root);
    document.removeEventListener('keydown', onKey);
    isShowing = false;
    try {
      chosen?.onPress?.();
    } catch (err) {
      // Don't let a button handler throw block the next queued alert.
      // eslint-disable-next-line no-console
      console.error('[Alert] button handler threw:', err);
    }
    processNext();
  };

  buttons.forEach((btn, i) => {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = '__eleplan_alert_btn';
    el.textContent = btn?.text || (i === 0 ? 'OK' : `Option ${i + 1}`);
    if (btn?.style) el.dataset.style = btn.style;
    // The last non-cancel button gets primary emphasis (iOS convention:
    // the rightmost / bottom-most action is the default).
    const isLast = i === buttons.length - 1;
    if (isLast && btn?.style !== 'cancel') {
      el.dataset.emphasis = 'primary';
    }
    el.addEventListener('click', () => dismiss(btn));
    actions.appendChild(el);
  });

  card.appendChild(actions);
  root.appendChild(card);

  const cancelBtn = findCancelButton(buttons);
  const cancelable = options?.cancelable !== false;

  // Backdrop click → cancel (only when there's an explicit cancel button
  // OR `cancelable` is true; matches Android's default and is safer than
  // committing the user to a destructive default on a stray click).
  root.addEventListener('mousedown', (e) => {
    if (e.target === root && (cancelBtn || cancelable)) {
      dismiss(cancelBtn || null);
    }
  });

  const onKey = (e) => {
    if (e.key === 'Escape' && (cancelBtn || cancelable)) {
      dismiss(cancelBtn || null);
    } else if (e.key === 'Enter') {
      // Activate the last (primary) button on Enter unless it's a cancel.
      const primary = [...buttons].reverse().find((b) => b && b.style !== 'cancel');
      if (primary) dismiss(primary);
    }
  };
  document.addEventListener('keydown', onKey);

  document.body.appendChild(root);
}

class Alert {
  /**
   * @param {string} title
   * @param {string} [message]
   * @param {Array<{ text?: string; onPress?: () => void; style?: 'default'|'cancel'|'destructive' }>} [buttons]
   * @param {{ cancelable?: boolean }} [options]
   */
  static alert(title, message, buttons, options) {
    const safeButtons =
      Array.isArray(buttons) && buttons.length > 0
        ? buttons
        : [{ text: 'OK', style: 'default' }];
    queue.push({
      title: title ?? '',
      message: message ?? '',
      buttons: safeButtons,
      options: options || null,
    });
    processNext();
  }

  // RN also exposes `prompt` on iOS — provide a basic fallback that
  // resolves via window.prompt so any rare call site doesn't no-op.
  static prompt(title, message, callbackOrButtons, _type, defaultValue) {
    const result = typeof window !== 'undefined' && typeof window.prompt === 'function'
      ? window.prompt([title, message].filter(Boolean).join('\n\n'), defaultValue ?? '')
      : null;
    if (typeof callbackOrButtons === 'function') {
      callbackOrButtons(result);
    } else if (Array.isArray(callbackOrButtons)) {
      const target = result === null
        ? callbackOrButtons.find((b) => b && b.style === 'cancel')
        : callbackOrButtons.find((b) => b && b.style !== 'cancel') || callbackOrButtons[0];
      try { target?.onPress?.(result); } catch (_) { /* swallow */ }
    }
  }
}

export default Alert;
export { Alert };
